import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  gDriveFilesForTree,
  gradeStringAtom,
  presentationsTreeIdMap,
  updatingAtom,
} from './atoms'
import { Table } from 'shared/ui/Table/Table'
import { PresentationFile } from './types'
import { Column } from 'shared/ui/Table/types'
import React, { useMemo, useState } from 'react'
import { Box, Button } from '@mui/material'
import {
  useDeletePresentationForFIle,
  useFileIndexesForTypes,
  useGetUpdatesForFile,
  useNavigateForMapping,
  useNodeForFile,
} from './hooks'
import { UpdateMethod, UpdateType } from '../nodeUpdates/utility'
import { UpdateData } from '../nodeUpdates/types'
import { NodeOperationsButton } from './NodeOperationsButton'

export function FilesForGradeTable() {
  const gDriveData = useAtomValue(gDriveFilesForTree)
  const columns = useTableColumns()
  const grade = useAtomValue(gradeStringAtom)
  const getUpdates = useGetUpdatesForFile()
  const [showUpdatableOnly, setShowUpdatableOnly] = useState(false)

  const items = useMemo(() => {
    if (!showUpdatableOnly) return gDriveData
    return gDriveData.filter((file) => getUpdates(file).length > 0)
  }, [gDriveData, showUpdatableOnly, getUpdates])

  if (!gDriveData.length)
    return <Box padding="10px">No Files Found For Grade {grade}</Box>

  return (
    <Table<PresentationFile>
      columns={columns}
      title={`${items.length} Files Found For Grade ${grade}`}
      items={items}
      toolBarItems={[
        <Button
          key="toggle-updatable"
          variant={showUpdatableOnly ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setShowUpdatableOnly((prev) => !prev)}
        >
          {showUpdatableOnly ? 'Show all' : 'Show updatable only'}
        </Button>,
      ]}
    />
  )
}

function useTableColumns(): Column<PresentationFile>[] {
  const typeCols = useTypeCols()
  return [
    {
      title: 'Name',
      rowContent({ name }) {
        return name
      },
    },
    ...typeCols,
    openPresentationCol(),
    ...useLessonCols(),
    usePresentationCol(),
    useUpdateCol(),
  ]
}

function useTypeCols() {
  const getIndexes = useFileIndexesForTypes()
  const parts = getIndexes('')
  const mappingNavigation = useNavigateForMapping()

  return parts.reduce<Column<PresentationFile>[]>((acc, stuff) => {
    const typePart = stuff[0]
    return [
      ...acc,
      {
        title: typePart,
        rowContent({ name }) {
          const fileMapping = getIndexes(name)
          const mapping = fileMapping.find((mapping) => mapping[0] === typePart)
          const { canNavigate, doNavigate } = mappingNavigation(
            typePart,
            fileMapping,
          )
          if (canNavigate) {
            return <Button onClick={doNavigate}>{mapping?.[1]}</Button>
          }
          return mapping?.[1]
        },
      },
    ]
  }, [])
}

function openPresentationCol() {
  return {
    title: 'Open Presentation',
    rowContent(file: PresentationFile) {
      return (
        <a
          href={`https://docs.google.com/presentation/d/${file.id}`}
          target="_blank"
          rel="noreferrer"
        >
          Go
        </a>
      )
    },
  }
}

function useLessonCols() {
  const idMap = useAtomValue(presentationsTreeIdMap)
  const getNode = useNodeForFile()
  return [
    {
      title: 'Lesson',
      rowContent(file: PresentationFile) {
        const node = getNode(file)
        return node?.title ?? 'Didnt Match'
      },
    },
  ]
}

function usePresentationCol() {
  const getDeletePresentationList = useDeletePresentationForFIle()
  const updating = useAtomValue(updatingAtom)

  return {
    title: 'Has Presentation',
    rowContent(file: PresentationFile) {
      const { hasLesson, deleteParams } = getDeletePresentationList(file)
      if (!hasLesson) return 'No Matched Lesson'
      if (!deleteParams) return 'No Presentation List'
      return (
        <NodeOperationsButton operations={[deleteParams]}>
          Delete
        </NodeOperationsButton>
      )
    },
  }
}

function isNodeCreateUpdate(update: UpdateData) {
  return (
    update.type === UpdateType.node && update.method === UpdateMethod.create
  )
}

function useUpdateCol() {
  const getUpdates = useGetUpdatesForFile()
  const getNode = useNodeForFile()
  return {
    title: 'Update',
    rowContent(file: PresentationFile) {
      const node = getNode(file)
      if (!node) return 'No Matched Lesson'
      const updates = getUpdates(file)
      if (!updates.length) return 'All Up To Date'
      const buttonTitle = isNodeCreateUpdate(updates[0])
        ? 'Create Presentation'
        : 'Update Presentation'

      return (
        <NodeOperationsButton operations={updates}>
          {buttonTitle}
        </NodeOperationsButton>
      )
    },
  }
}
